import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import {
  Type,
  Heading,
  MousePointerClick,
  Square,
  Space,
  Minus,
  Megaphone,
  Code,
  Calendar,
  CalendarPlus,
  Package,
  MessageCircle,
  MessageSquare,
  FileText,
  ClipboardList,
  NotebookPen,
  Search,
  LayoutList,
  UserPen,
  BookOpen,
  CreditCard,
  Upload,
  Zap,
  Activity,
  Frame,
  Receipt,
} from "lucide-react";
import { BLOCK_TYPES, BLOCK_CATEGORIES, type BlockType, type BlockCategory } from "../../../shared/schema";

interface BlockTypeSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (type: BlockType) => void;
}

// Map block types to icons
const blockIcons: Record<BlockType, React.ComponentType<{ className?: string }>> = {
  text: Type,
  heading: Heading,
  button: MousePointerClick,
  card: Square,
  section: LayoutList,
  spacer: Space,
  divider: Minus,
  banner: Megaphone,
  html: Code,
  "rich-text": Type,
  popup: MessageSquare,
  "appointment-list": Calendar,
  "order-list": Package,
  "chatroom-list": MessageCircle,
  "file-list": FileText,
  "form-response-list": ClipboardList,
  "pinned-forms-list": NotebookPen,
  "profile-edit": UserPen,
  "content-list": BookOpen,
  "purchase-list": CreditCard,
  "file-upload": Upload,
  "stripe-subscriptions": Zap,
  "stripe-billing-portal-button": Receipt,
  "stripe-invoices": Receipt,
  "vital-data": Activity,
  "appointment-booking": CalendarPlus,
  iframe: Frame,
};

export const BlockTypeSelector: React.FC<BlockTypeSelectorProps> = ({ open, onOpenChange, onSelect }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");

  const handleSelect = (type: BlockType) => {
    onSelect(type);
    onOpenChange(false);
    setSearchQuery("");
  };

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
    if (!newOpen) {
      setSearchQuery("");
    }
  };

  // Filter blocks by search query
  const filteredBlocks = useMemo(() => {
    if (!searchQuery.trim()) {
      return BLOCK_TYPES;
    }
    const query = searchQuery.toLowerCase();
    return BLOCK_TYPES.filter(
      (block) =>
        block.label.toLowerCase().includes(query) ||
        block.description.toLowerCase().includes(query) ||
        block.type.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Group filtered blocks by category
  const blocksByCategory = useMemo(() => {
    const grouped: Record<BlockCategory, typeof BLOCK_TYPES> = {
      content: [],
      layout: [],
      data: [],
      integrations: [],
    };
    filteredBlocks.forEach((block) => {
      grouped[block.category].push(block);
    });
    return grouped;
  }, [filteredBlocks]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("Add Block")}</DialogTitle>
          <DialogDescription>{t("Choose a block type to add to the page")}</DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("Search blocks...")}
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <TooltipProvider delayDuration={300}>
          <div className="py-2 max-h-[60vh] overflow-y-auto">
            {filteredBlocks.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">{t("No blocks found")}</p>
            ) : (
              BLOCK_CATEGORIES.map(({ category, label }) => {
                const categoryBlocks = blocksByCategory[category];
                if (categoryBlocks.length === 0) return null;

                return (
                  <div key={category} className="mb-4">
                    <h3 className="text-sm font-medium text-muted-foreground mb-2 px-1">{label}</h3>
                    <div className="grid grid-cols-3 gap-2">
                      {categoryBlocks.map(({ type, label: blockLabel, description }) => {
                        const Icon = blockIcons[type];
                        return (
                          <Tooltip key={type}>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                className="h-auto flex-col items-center justify-center p-3 gap-1.5"
                                onClick={() => handleSelect(type)}
                              >
                                <Icon className="h-5 w-5 text-muted-foreground" />
                                <span className="text-xs font-medium text-center leading-tight">{blockLabel}</span>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              <p>{description}</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
};
